// Reduce una imagen a la medida de su lienzo ANTES de subirla.
//
// Una imagen se descomprime entera en la app aunque se pinte pequeña: el fondo
// de un banner de prueba de 5932 × 3955 px (pintado a 1518 × 253) eran 94 MB en
// la ventana, unos 240 MB en el proceso gráfico y 190 MB de memoria de vídeo
// (oct-2026). El Inicio lo abre todo el mundo: lo que se sube aquí tiene que
// llegar ya a su tamaño.
//
// No recorta ni cambia de formato: solo encoge, con la misma proporción. Así el
// encuadre es el de siempre (la app pinta el fondo con «cover» centrado, y el
// CSS a medida de un banner puede moverlo).

// Formatos que se saben reducir sin perder nada por el camino. Un GIF no se
// toca (se quedaría en su primer fotograma).
const SHRINKABLE = new Set(['image/png', 'image/jpeg', 'image/webp']);

// Hasta el doble del lienzo por lado: lo que aprovecha una pantalla con la
// escala al 200 %.
export const DENSITY = 2;

// Por debajo de esto no merece la pena volver a comprimir (se perdería calidad
// para ahorrar casi nada).
const WORTH_IT = 0.9;

const LOSSY_QUALITY = 0.9;

/**
 * A qué medida se queda una imagen de `width` × `height` para un lienzo.
 * Como mucho ocupa los píxeles del lienzo al doble por lado (DENSITY), pero
 * nunca tan pequeña que deje de cubrirlo, y nunca se amplía.
 * Devuelve { width, height, shrink } (shrink = false: se sube tal cual).
 */
export function targetSize(width, height, surface) {
  const w = Math.floor(Number(width) || 0);
  const h = Math.floor(Number(height) || 0);
  const sw = Number(surface?.width) || 0;
  const sh = Number(surface?.height) || 0;
  if (w <= 0 || h <= 0 || sw <= 0 || sh <= 0) return { width: w, height: h, shrink: false };
  const budget = sw * DENSITY * sh * DENSITY;
  const byBudget = Math.sqrt(budget / (w * h));
  const toCover = Math.max(sw / w, sh / h);
  const scale = Math.max(byBudget, toCover);
  if (scale >= WORTH_IT) return { width: w, height: h, shrink: false };
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)), shrink: true };
}

function ascii(bytes, at, length) {
  let out = '';
  for (let i = 0; i < length && at + i < bytes.length; i++) out += String.fromCharCode(bytes[at + i]);
  return out;
}

/**
 * ¿Es una imagen animada? (bytes del archivo + su tipo). Una animada no se
 * reduce: al pasarla por un lienzo se quedaría quieta.
 */
export function isAnimated(bytes, type) {
  if (!bytes || !bytes.length) return false;
  if (type === 'image/gif') return true;
  if (type === 'image/webp') {
    // RIFF....WEBPVP8X + banderas: el bit 0x02 es «animación».
    return ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP' && ascii(bytes, 12, 4) === 'VP8X' && (bytes[20] & 0x02) !== 0;
  }
  if (type === 'image/png') {
    // APNG: un trozo «acTL» antes del primer «IDAT».
    let at = 8;
    while (at + 8 <= bytes.length) {
      const length = ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;
      const name = ascii(bytes, at + 4, 4);
      if (name === 'acTL') return true;
      if (name === 'IDAT' || name === 'IEND') return false;
      at += 12 + length;
    }
  }
  return false;
}

/**
 * Deja `file` a la medida de `surface` ({ width, height }, el lienzo donde se
 * pinta). Devuelve { file, resized, from, to }: si no hay nada que reducir (o
 * no se puede: animada, formato sin soporte, fallo al leerla) devuelve el mismo
 * archivo con `resized: false`. Nunca impide la subida.
 */
export async function shrinkToSurface(file, surface) {
  const same = { file, resized: false, from: null, to: null };
  if (!file || !surface || !SHRINKABLE.has(file.type)) return same;
  let bitmap = null;
  try {
    if (isAnimated(new Uint8Array(await file.arrayBuffer()), file.type)) return same;
    bitmap = await createImageBitmap(file);
    const from = { width: bitmap.width, height: bitmap.height };
    const to = targetSize(from.width, from.height, surface);
    if (!to.shrink) return { ...same, from };
    const canvas = document.createElement('canvas');
    canvas.width = to.width;
    canvas.height = to.height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, to.width, to.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, file.type, file.type === 'image/png' ? undefined : LOSSY_QUALITY));
    // Un navegador que no sabe escribir ese formato devuelve PNG: entonces el
    // nombre y el tipo dejarían de casar, y se sube el original.
    if (!blob || blob.type !== file.type) return { ...same, from };
    return {
      file: new File([blob], file.name, { type: file.type, lastModified: Date.now() }),
      resized: true,
      from,
      to: { width: to.width, height: to.height }
    };
  } catch (_) {
    return same;
  } finally {
    bitmap?.close?.();
  }
}
